import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { RequirementNode, RequirementType, RequirementPriorityLevel, RequirementStatus } from '@ispot/shared'
import { InlineSelect } from '../InlineEditors'
import { PriorityCell, StatusCell } from '../cells'
import {
  REQUIREMENT_TYPE_LABELS,
  REQUIREMENT_PRIORITY_LABELS,
  REQUIREMENT_STATUS_LABELS,
  getAllowedRequirementTypesAtDepth,
} from '../constants'

interface Props {
  node: RequirementNode
  onUpdate: (patch: Partial<RequirementNode>) => void
}

type EditField = 'type' | 'priority' | 'status' | null

export function DetailMetaStrip({ node, onUpdate }: Props) {
  const [editing, setEditing] = useState<EditField>(null)

  const allowedTypes = Object.fromEntries(
    getAllowedRequirementTypesAtDepth(node.depth).map(t => [t, REQUIREMENT_TYPE_LABELS[t]]),
  ) as Record<RequirementType, string>

  return (
    <div
      className="px-5 py-1 flex-shrink-0"
      style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
    >
      {/* Type */}
      <PropRow label="Type">
        {editing === 'type' ? (
          <InlineSelect<RequirementType>
            value={node.type as RequirementType}
            options={allowedTypes}
            onSave={(v) => { onUpdate({ type: v }); setEditing(null) }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <MetaControl onClick={() => setEditing('type')}>
            {REQUIREMENT_TYPE_LABELS[node.type]}
          </MetaControl>
        )}
      </PropRow>

      {/* Priority */}
      <PropRow label="Priority">
        {editing === 'priority' ? (
          <InlineSelect<RequirementPriorityLevel>
            value={node.priority ?? 'medium'}
            options={REQUIREMENT_PRIORITY_LABELS}
            onSave={(v) => { onUpdate({ priority: v }); setEditing(null) }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <MetaControl onClick={() => setEditing('priority')}>
            <PriorityCell priority={node.priority} />
          </MetaControl>
        )}
      </PropRow>

      {/* Status */}
      <PropRow label="Status">
        {editing === 'status' ? (
          <InlineSelect<RequirementStatus>
            value={node.status}
            options={REQUIREMENT_STATUS_LABELS}
            onSave={(v) => { onUpdate({ status: v }); setEditing(null) }}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <MetaControl onClick={() => setEditing('status')}>
            <StatusCell status={node.status} />
          </MetaControl>
        )}
      </PropRow>

      {/* Updated (read-only) */}
      <PropRow label="Updated">
        <span style={{ fontSize: 12.5, color: 'var(--color-fg-secondary)' }}>
          {formatRelative(node.updatedAt)}
        </span>
      </PropRow>
    </div>
  )
}

function PropRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between" style={{ minHeight: 30, fontSize: 12.5 }}>
      <span style={{ color: 'var(--color-fg-muted)' }}>{label}</span>
      <div className="flex items-center">{children}</div>
    </div>
  )
}

function MetaControl({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 border-0 bg-transparent cursor-pointer rounded"
      style={{
        padding: '2px 6px',
        margin: '0 -6px',
        color: 'var(--color-fg)',
        fontSize: 12.5,
        transition: 'background 120ms',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      onClick={onClick}
    >
      {children}
      <ChevronDown size={11} style={{ color: 'var(--color-fg-muted)', flexShrink: 0 }} />
    </button>
  )
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const ms = Date.now() - then
  if (ms < 60_000) return 'just now'
  const mins = Math.floor(ms / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  if (days < 30) return `${Math.floor(days / 7)}w ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}
